'use client';

import { useCallback, useMemo } from 'react';
import type { CommentWithRepliesFragment } from '@frameio/graphql-client/generated';
import { useActiveViewableAssetId } from '@frameio/shared/hooks';
import { useActiveDocumentId } from '../../../shared/contexts/ActiveHostEntitiesContext';
import { useGetAllCompositionExportsXmpMetadataQuery } from '../../gql';
import type { GetAllCompositionExportsXmpMetadataQuery } from '../../gql/queries/generated/GetAllCompositionExportsXmpMetadata';

export type AeCompositionExportForMarkerSync =
  GetAllCompositionExportsXmpMetadataQuery['getAllCompositionExportsXmpMetadata'][number];

function normalizeWorkAreaStartMicroseconds(
  raw: AeCompositionExportForMarkerSync['workAreaStart']
): number | null {
  if (raw == null) {
    return null;
  }
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return Math.round(n);
}

/**
 * XMP export row for the given asset on the given composition (at most one per
 * pair in normal flows). Used to recover render time-span for marker sync.
 */
export function findCompositionExportMetadataForMarkerSync(
  entries: GetAllCompositionExportsXmpMetadataQuery['getAllCompositionExportsXmpMetadata'],
  assetId: string,
  compositionId: string
): AeCompositionExportForMarkerSync | null {
  return (
    entries.find(
      (e) =>
        e.assetId === assetId &&
        e.compositionId != null &&
        String(e.compositionId) === String(compositionId)
    ) ?? null
  );
}

export function shouldApplyAeExportWorkAreaOffset(
  meta: AeCompositionExportForMarkerSync | null | undefined
): meta is AeCompositionExportForMarkerSync & { workAreaStart: number } {
  return normalizeWorkAreaStartMicroseconds(meta?.workAreaStart) != null;
}

/**
 * Maps a Frame.io comment timestamp (µs, relative to the rendered asset) to
 * composition-timeline µs using the export's work-area start, mirroring
 * Premiere's `sequenceIn` offset in {@link getPremiereCommentMarkerStartMicroseconds}.
 */
export function getAfterEffectsCommentMarkerStartMicroseconds(
  comment: Pick<CommentWithRepliesFragment, 'timestampMicroseconds'>,
  exportMetadata: AeCompositionExportForMarkerSync | null | undefined
): number {
  const baseTimestamp = comment.timestampMicroseconds || 0;
  const offset = normalizeWorkAreaStartMicroseconds(
    exportMetadata?.workAreaStart
  );
  if (offset == null) {
    return baseTimestamp;
  }
  return baseTimestamp + offset;
}

export function useAfterEffectsCommentMarkerSyncState({
  assetId: assetIdOverride,
}: {
  assetId: string | null | undefined;
}): {
  /** XMP export row for an asset on the active composition (undefined when asset or comp is missing). */
  getExportMetadataForAsset: (
    targetAssetId: string | null | undefined
  ) => AeCompositionExportForMarkerSync | null | undefined;
  isActiveDocument: boolean;
} {
  const assetIdFromView = useActiveViewableAssetId();
  const assetId = assetIdOverride ?? assetIdFromView;
  const compositionId = useActiveDocumentId();

  const { data } = useGetAllCompositionExportsXmpMetadataQuery({
    context: { resolveWithUXP: true },
    skip: !compositionId,
  });

  const entries = useMemo(
    () => data?.getAllCompositionExportsXmpMetadata ?? [],
    [data?.getAllCompositionExportsXmpMetadata]
  );

  const getExportMetadataForAsset = useCallback(
    (targetAssetId: string | null | undefined) => {
      if (!compositionId || !targetAssetId) {
        return undefined;
      }
      return findCompositionExportMetadataForMarkerSync(
        entries,
        targetAssetId,
        compositionId
      );
    },
    [compositionId, entries]
  );

  const isActiveDocument = Boolean(compositionId && assetId);

  return {
    getExportMetadataForAsset,
    isActiveDocument,
  };
}
